import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Checkbox,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus, IconRoute, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listItems } from '../items/api';
import { listLots, type LotDto } from './api';
import { LotFormModal } from './LotFormModal';

/**
 * Lots.
 *
 * **Soonest to expire first**, because that is what the list is for. A lot with no expiry sorts
 * last rather than first, which is what a plain sort on a nullable date would do — the server
 * orders it that way and this screen does not re-sort it.
 *
 * **An empty lot still appears.** A lot registered this morning for goods arriving tomorrow has
 * nothing anywhere and is exactly the lot its registrar wants to see. "Only with stock" is there
 * for the other question.
 *
 * **The quantity is the session's warehouse**, not the tenant's. A lot belongs to an item and the
 * same lot can sit in two buildings; "how much is here" is what somebody on the floor is asking.
 */
export function LotsRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const warehouseId = useCurrentWarehouseId();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<LotDto | null>(null);
  const [search, setSearch] = useState('');
  const [onlyWithStock, setOnlyWithStock] = useState(false);

  // Every other list screen fetches once and filters in the browser. This one cannot: lots are
  // the one thing that runs to thousands, because every receipt of a lot-controlled item makes
  // another one. Asking the server means a query per keystroke unless the typing is allowed to
  // settle first. The checkbox is not debounced — a click is already a finished thought.
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const canRegister = permissions.has('lots:register');

  const query = useQuery({
    queryKey: ['lots', warehouseId, debouncedSearch, onlyWithStock] as const,
    queryFn: ({ signal }) => listLots(
      { search: debouncedSearch.trim() || null, onlyWithStock }, signal,
    ),
    enabled: Boolean(warehouseId),
  });

  // Only lot-controlled items can have a lot, so the picker offers only those.
  const items = useQuery({
    queryKey: ['items', 'lot-controlled'] as const,
    queryFn: ({ signal }) => listItems(false, signal),
    enabled: modalOpen,
  });

  const itemOptions = useMemo(
    () => (items.data ?? [])
      .filter((i) => i.isLotControlled)
      .map((i) => ({ value: i.itemId, label: `${i.itemNumber} — ${i.description}` })),
    [items.data],
  );

  const rows = query.data?.rows ?? [];

  const columns: DataColumn<LotDto>[] = useMemo(() => [
    {
      key: 'lotNumber',
      label: t('lots.lotNumber'),
      card: 'title',
      render: (l) => <Code>{l.lotNumber}</Code>,
    },
    {
      key: 'item',
      label: t('lots.item'),
      card: 'subtitle',
      render: (l) => (
        <Stack gap={0}>
          <Text size="sm">{l.itemNumber}</Text>
          {l.itemDescription ? <Text size="xs" c="dimmed">{l.itemDescription}</Text> : null}
        </Stack>
      ),
    },
    {
      key: 'quantity',
      label: t('lots.onHand'),
      align: 'right',
      render: (l) => `${l.quantityOnHand} ${l.stockingUomCode}`,
    },
    {
      key: 'expiry',
      label: t('lots.expiry'),
      card: 'aside',
      render: (l) => {
        if (!l.expirationDateUtc) return <Text size="sm" c="dimmed">—</Text>;
        const when = new Date(l.expirationDateUtc).toLocaleDateString();
        // Expired is said out loud rather than left to be worked out from a date.
        return l.isExpired
          ? <Badge size="sm" color="red" variant="light">{t('lots.expiredOn', { date: when })}</Badge>
          : <Text size="sm">{when}</Text>;
      },
    },
    {
      key: 'supplierLot',
      label: t('lots.supplierLot'),
      render: (l) => l.supplierLotNumber ?? <Text size="sm" c="dimmed">—</Text>,
    },
  ], [t]);

  if (!warehouseId) {
    return (
      <Stack>
        <PageHeader title={t('nav.lots')} />
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">{t('lots.chooseWarehouse')}</Alert>
      </Stack>
    );
  }

  return (
    <>
      <PageHeader
        title={t('nav.lots')}
        actions={canRegister ? (
          <Button
            color="invetrak"
            leftSection={<IconPlus size={16} />}
            onClick={() => { setEditing(null); open(); }}
          >
            {t('lots.register')}
          </Button>
        ) : null}
      />

      <Stack gap="md">
        <Text size="sm" c="dimmed">{t('lots.intro')}</Text>

        <Group align="flex-end">
          <TextInput
            label={t('lots.search')}
            description={t('lots.searchHint')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 320 }}
          />
          <Checkbox
            label={t('lots.onlyWithStock')}
            checked={onlyWithStock}
            onChange={(e) => setOnlyWithStock(e.currentTarget.checked)}
            pb={8}
          />
        </Group>

        {query.isLoading && <Center py="xl"><Loader color="invetrak" /></Center>}

        {query.isError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {query.error instanceof ApiError ? query.error.problem.title : t('lots.errors.loadFailed')}
          </Alert>
        )}

        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">{t('lots.empty')}</Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <>
            <ResponsiveTable
              rows={rows}
              columns={columns}
              rowKey={(l) => l.lotId}
              minWidth={720}
              actionsWidth={96}
              striped
              highlightOnHover
              // The trace is offered to anyone who can see the lot: during a recall
              // the person asking "who got this" is not necessarily the person who
              // may correct an expiry date.
              actions={(l) => (
                <Group gap={4} wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    color="invetrak"
                    aria-label={t('lots.trace', { lot: l.lotNumber })}
                    onClick={() => void navigate({ to: '/app/lots/$id/trace', params: { id: l.lotId } })}
                  >
                    <IconRoute size={16} />
                  </ActionIcon>
                  {canRegister && (
                    <ActionIcon
                      variant="subtle"
                      color="invetrak"
                      aria-label={t('lots.edit', { lot: l.lotNumber })}
                      onClick={() => { setEditing(l); open(); }}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  )}
                </Group>
              )}
            />
            {/* Said plainly: a page of 200 out of thousands is not the whole answer, and a grid
                that silently shows the first page is how somebody concludes a lot is missing. */}
            {query.data!.totalCount > rows.length && (
              <Text size="xs" c="dimmed">
                {t('lots.showing', { shown: rows.length, total: query.data!.totalCount })}
              </Text>
            )}
          </>
        )}
      </Stack>

      <LotFormModal
        opened={modalOpen}
        onClose={close}
        lot={editing}
        itemOptions={itemOptions}
        itemsLoading={items.isLoading}
        onSaved={() => void queryClient.invalidateQueries({ queryKey: ['lots'] })}
      />
    </>
  );
}
